import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { Schemas } from '@npc/api-client'

import { api } from '../../app/api'
import { call } from '../../app/errors'

export type LuluAgent = Schemas['LuluAgent']
/** 职责标签闭集，与 openapi enum 同源（改那边这里跟着变）。 */
export type LuluResponsibility = LuluAgent['responsibilities'][number]

/** 闭集全列，供多选。顺序按真实研发流程：从想清楚到发出去。 */
export const LULU_RESPONSIBILITIES: readonly LuluResponsibility[] = [
  'product_design',
  'ui_design',
  'architect',
  'frontend',
  'backend',
  'qa',
  'review',
  'release',
  'research',
  'ops',
]

/**
 * Agent 库。
 *
 * `include_disabled` 默认真：停用是**软删**，历史都还在，
 * 库里看不见它反而会让人以为它被删干净了，然后再建一个同名的。
 */
export function useAgentLibrary(includeDisabled = true) {
  return useQuery({
    queryKey: ['lulu-agent-library', includeDisabled],
    queryFn: () =>
      call('/agents', () =>
        api.GET('/agents', {
          params: { query: { scope: 'account', include_disabled: includeDisabled } },
        }),
      ),
  })
}

export type LuluAgentSuggestion = Schemas['LuluAgentSuggestion']

/**
 * 「他负责什么」→ 职责标签 + 执行器。
 *
 * 只读，不写任何东西：产出是一份建议，保存仍旧是他点的那一下。
 * 失败不该挡住建 Agent——调用方拿到错误只是少了一次自动填写。
 */
export function useSuggestAgentSetup() {
  return useMutation({
    mutationFn: (input: { readonly description: string; readonly displayName?: string }) =>
      call('/agents/suggest', () =>
        api.POST('/agents/suggest', {
          body: {
            description: input.description,
            ...(input.displayName && input.displayName.trim() !== ''
              ? { display_name: input.displayName.trim() }
              : {}),
          },
        }),
      ),
  })
}

/** 建一个。`display_name` 留空由服务端按 role 生成——**不拿模型名凑**。 */
export function useCreateAgent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      readonly displayName: string
      readonly role: string
      /** 职责标签（闭集）。空数组不发这个键——请求体形状与加标签之前逐字一致。 */
      readonly responsibilities?: readonly LuluResponsibility[]
      /**
       * 干活用哪个执行器。**这一格空着，这位 Agent 派不到活**——
       * 派活第一步看职责名册，第二步看这个绑定，两处缺一处他都不在场。
       */
      readonly executorMaintainerId?: string
    }) =>
      call('/agents', () =>
        api.POST('/agents', {
          body: {
            scope: 'account',
            ...(input.displayName.trim() === '' ? {} : { display_name: input.displayName.trim() }),
            ...(input.role.trim() === '' ? {} : { role: input.role.trim() }),
            ...(input.responsibilities && input.responsibilities.length > 0
              ? { responsibilities: [...input.responsibilities] }
              : {}),
            ...(input.executorMaintainerId === undefined
              ? {}
              : { executor_maintainer_id: input.executorMaintainerId }),
          },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lulu-agent-library'] }),
  })
}

/** 改配置。服务端会生成新 revision，运行中的会话固定在旧 revision 上。 */
export function useUpdateAgent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      readonly agentId: string
      readonly displayName?: string
      readonly role?: string
      readonly responsibilities?: readonly LuluResponsibility[]
      /** 给 uuid 就绑执行器；给全零 uuid 就解绑；不给就不动。 */
      readonly executorMaintainerId?: string
    }) =>
      call('/agents/{agentId}', () =>
        api.PATCH('/agents/{agentId}', {
          params: { path: { agentId: input.agentId } },
          body: {
            ...(input.displayName === undefined ? {} : { display_name: input.displayName }),
            ...(input.role === undefined ? {} : { role: input.role }),
            ...(input.responsibilities === undefined
              ? {}
              : { responsibilities: [...input.responsibilities] }),
            ...(input.executorMaintainerId === undefined
              ? {}
              : { executor_maintainer_id: input.executorMaintainerId }),
          },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lulu-agent-library'] }),
  })
}

/**
 * 停用。后端是**软删且幂等**——所以界面上写「停用」不写「删除」。
 *
 * 写「删除」会让人以为它做过的事也跟着没了，而那些证据都还在。
 */
export function useDisableAgent() {
  const qc = useQueryClient()
  return useMutation({
    /**
     * 停用一位 Agent。
     *
     * **`acknowledge` 缺省是 false**：第一次点永远先去问一遍。
     * 后端手上还有正在跑的活或活着的会话时回 409 并带四档影响面，
     * 界面把它说成人话再给一颗「还是停用」；那一颗才带 acknowledge=true。
     *
     * 不在这里吞 409：让它抛出去，屏那侧读 `ApiFailure.details`。
     * 吞掉的话「有东西挡着」和「停成了」在调用方看来是同一件事。
     */
    mutationFn: ({ agentId, acknowledge }: { agentId: string; acknowledge?: boolean }) =>
      call('/agents/{agentId}', () =>
        api.DELETE('/agents/{agentId}', {
          params: {
            path: { agentId },
            ...(acknowledge ? { query: { acknowledge_impact: true } } : {}),
          },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lulu-agent-library'] }),
  })
}
